import React, { useCallback, useEffect, useState } from 'react';
import {
  AuditOutlined,
  ClearOutlined,
  CopyOutlined,
  DatabaseOutlined,
  DownloadOutlined,
  LinkOutlined,
  LockOutlined,
  MailOutlined,
  ReloadOutlined,
  SendOutlined,
  RocketOutlined,
  SearchOutlined,
  SettingOutlined,
  StopOutlined,
  TeamOutlined,
  UnlockOutlined,
  UserDeleteOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Modal, message } from 'antd';
import * as api from '../api/client';
import type { AdminUser, AdminMetrics, AuditEvent, BetaInvite } from '../api/client';
import type { GoogleUser } from '../App';
import AdminPendingList from './AdminPendingList';

interface Props {
  user: GoogleUser | null;
}

type AdminTab = 'users' | 'invites' | 'metrics' | 'audit' | 'requests';

const STATUS_COLOR: Record<string, string> = {
  active:  '#10b981',
  pending: '#38bdf8',
  locked:  '#f59e0b',
  removed: '#6b7280',
};

const STATUS_LABEL: Record<string, string> = {
  active:  'Active',
  pending: 'Pending',
  locked:  'Locked',
  removed: 'Removed',
};

// ---------------------------------------------------------------------------
// Metrics panel
// ---------------------------------------------------------------------------

const MetricsPanel: React.FC<{ metrics: AdminMetrics }> = ({ metrics }) => {
  const users = metrics?.users ?? { total_discovered: 0, active: 0, pending: 0, locked: 0, removed: 0 };
  const tasks = metrics?.tasks ?? { total: 0, running: 0, completed: 0, failed: 0 };
  const kbs = metrics?.knowledge_bases ?? { total: 0, documents: 0, chunks: 0 };
  const agents = metrics?.agents ?? { total: 0, executions_total: 0 };
  const memory = metrics?.memory ?? { total: 0 };
  const tokens = metrics?.api_tokens ?? { total: 0, active: 0 };
  return (
    <div className="praxys-admin-metrics-grid">
      <div className="praxys-admin-metric-card">
        <TeamOutlined className="praxys-admin-metric-icon" />
        <div className="praxys-admin-metric-value">{users.total_discovered}</div>
        <div className="praxys-admin-metric-label">Total Users</div>
        <div className="praxys-admin-metric-sub">
          {users.active} active · {users.pending ?? 0} pending · {users.locked} locked · {users.removed} removed
        </div>
      </div>
      <div className="praxys-admin-metric-card">
        <SettingOutlined className="praxys-admin-metric-icon" />
        <div className="praxys-admin-metric-value">{tasks.total}</div>
        <div className="praxys-admin-metric-label">Total Tasks</div>
        <div className="praxys-admin-metric-sub">
          {tasks.running} running · {tasks.completed} done · {tasks.failed} failed
        </div>
      </div>
      <div className="praxys-admin-metric-card">
        <DatabaseOutlined className="praxys-admin-metric-icon" />
        <div className="praxys-admin-metric-value">{kbs.total}</div>
        <div className="praxys-admin-metric-label">Knowledge Bases</div>
        <div className="praxys-admin-metric-sub">
          {kbs.documents} docs · {kbs.chunks} chunks
        </div>
      </div>
      <div className="praxys-admin-metric-card">
        <RocketOutlined className="praxys-admin-metric-icon" />
        <div className="praxys-admin-metric-value">{agents.total}</div>
        <div className="praxys-admin-metric-label">Agents</div>
        <div className="praxys-admin-metric-sub">
          {agents.executions_total} total executions
        </div>
      </div>
      <div className="praxys-admin-metric-card">
        <AuditOutlined className="praxys-admin-metric-icon" />
        <div className="praxys-admin-metric-value">{memory.total}</div>
        <div className="praxys-admin-metric-label">Memory Entries</div>
        <div className="praxys-admin-metric-sub">
          {tokens.active} active tokens of {tokens.total}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Beta invites panel
// ---------------------------------------------------------------------------

const BetaInvitesPanel: React.FC<{ adminEmail: string }> = ({ adminEmail }) => {
  const [invites, setInvites] = useState<BetaInvite[]>([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [note, setNote] = useState('');
  const [latestLink, setLatestLink] = useState('');

  const fetchInvites = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listBetaInvites(adminEmail);
      setInvites(Array.isArray(res.data?.invites) ? res.data.invites : []);
    } catch (e: any) {
      message.error('Failed to load invites: ' + (e?.response?.data?.detail || e?.message));
    } finally {
      setLoading(false);
    }
  }, [adminEmail]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const copyText = async (text: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      message.success('Invite link copied');
    } catch {
      message.error('Could not copy link');
    }
  };

  const handleCreate = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      message.warning('Enter an email address');
      return;
    }
    setLoading(true);
    try {
      const res = await api.createBetaInvite(adminEmail, {
        email: trimmedEmail,
        display_name: displayName.trim(),
        note: note.trim(),
      });
      const link = res.data?.invite?.invite_link || '';
      setLatestLink(link);
      setEmail('');
      setDisplayName('');
      setNote('');
      await fetchInvites();
      if (res.data?.email_sent) {
        message.success('Beta invite created and emailed');
      } else {
        message.warning('Invite created, but email was not sent: ' + (res.data?.email_error || 'SMTP not configured'));
      }
      if (link) void copyText(link);
    } catch (e: any) {
      message.error('Failed to create invite: ' + (e?.response?.data?.detail || e?.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (invite: BetaInvite) => {
    setLoading(true);
    try {
      await api.revokeBetaInvite(adminEmail, invite.id);
      message.success('Invite revoked');
      await fetchInvites();
    } catch (e: any) {
      message.error('Failed to revoke invite: ' + (e?.response?.data?.detail || e?.message));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (invite: BetaInvite) => {
    setLoading(true);
    try {
      const res = await api.resendBetaInvite(adminEmail, invite.id);
      const link = res.data?.invite?.invite_link || '';
      if (link) setLatestLink(link);
      if (res.data?.email_sent) {
        message.success('Invite email resent');
      } else {
        message.warning('Invite was not emailed: ' + (res.data?.email_error || 'SMTP not configured'));
      }
      await fetchInvites();
    } catch (e: any) {
      message.error('Failed to resend invite: ' + (e?.response?.data?.detail || e?.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="praxys-admin-invites">
      <div className="praxys-admin-invite-form">
        <input
          className="praxys-admin-search"
          placeholder="invitee@example.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
        <input
          className="praxys-admin-search"
          placeholder="Display name"
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
        />
        <input
          className="praxys-admin-search"
          placeholder="Internal note"
          value={note}
          onChange={e => setNote(e.target.value)}
        />
        <button type="button" className="praxys-admin-btn" onClick={handleCreate} disabled={loading}>
          <MailOutlined /> Create Invite
        </button>
        <button type="button" className="praxys-admin-btn" onClick={fetchInvites} disabled={loading}>
          <ReloadOutlined /> Refresh
        </button>
      </div>

      {latestLink && (
        <div className="praxys-admin-invite-link">
          <LinkOutlined />
          <span>{latestLink}</span>
          <button type="button" className="praxys-admin-btn" onClick={() => copyText(latestLink)}>
            <CopyOutlined /> Copy
          </button>
        </div>
      )}

      {loading && invites.length === 0 ? (
        <div className="praxys-view-empty">Loading invites...</div>
      ) : invites.length === 0 ? (
        <div className="praxys-view-empty">No beta invites have been created yet.</div>
      ) : (
        <div className="praxys-admin-user-list">
          {invites.map(invite => (
            <div key={invite.id} className="praxys-admin-user-row">
              <div className="praxys-admin-user-info">
                <div className="praxys-admin-user-avatar">
                  {(invite.display_name || invite.email).charAt(0).toUpperCase()}
                </div>
                <div className="praxys-admin-user-copy">
                  <div className="praxys-admin-user-id">{invite.email}</div>
                  {invite.display_name && <div className="praxys-admin-user-notes">{invite.display_name}</div>}
                  {invite.note && <div className="praxys-admin-user-stats">{invite.note}</div>}
                  {invite.accepted_by_user_id && (
                    <div className="praxys-admin-user-stats">
                      Accepted by {invite.accepted_by_user_id}
                    </div>
                  )}
                  <div className="praxys-admin-user-stats">
                    Sent {invite.email_sent_count || 0} time{invite.email_sent_count === 1 ? '' : 's'}
                    {invite.last_sent_at ? ` · last ${new Date(invite.last_sent_at).toLocaleString()}` : ''}
                  </div>
                  {invite.last_email_error && (
                    <div className="praxys-admin-invite-error">
                      Email issue: {invite.last_email_error}
                    </div>
                  )}
                </div>
              </div>
              <div className="praxys-admin-user-mid">
                <span
                  className="praxys-admin-status-badge"
                  style={{
                    background: invite.status === 'pending' ? '#38bdf822' : '#6b728022',
                    color: invite.status === 'pending' ? '#38bdf8' : '#9ca3af',
                  }}
                >
                  {invite.status}
                </span>
                <span className="praxys-admin-user-since">
                  {new Date(invite.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="praxys-admin-user-actions">
                <button
                  type="button"
                  className="praxys-admin-btn"
                  onClick={() => handleResend(invite)}
                  disabled={loading}
                >
                  <SendOutlined /> Resend
                </button>
                {invite.status === 'pending' && (
                  <button
                    type="button"
                    className="praxys-admin-btn praxys-admin-btn--danger"
                    onClick={() => handleRevoke(invite)}
                    disabled={loading}
                  >
                    <StopOutlined /> Revoke
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Audit log panel
// ---------------------------------------------------------------------------

const AuditPanel: React.FC<{ events: AuditEvent[] }> = ({ events }) => (
  <div className="praxys-admin-audit-list">
    {(events ?? []).length === 0 ? (
      <div className="praxys-view-empty">No admin audit events yet.</div>
    ) : (
      (events ?? []).map(ev => {
        let meta: any = {};
        try { meta = JSON.parse(ev.metadata_json || '{}'); } catch { /* ignore */ }
        return (
          <div key={ev.id} className="praxys-admin-audit-item">
            <div className="praxys-admin-audit-type">{ev.event_type}</div>
            <div className="praxys-admin-audit-detail">
              <span className="praxys-admin-audit-entity">{ev.entity_id}</span>
              {ev.actor_user_id ? <span> · by <strong>{ev.actor_user_id}</strong></span> : null}
              {meta.reason ? <span> · <em>{meta.reason}</em></span> : null}
            </div>
            <div className="praxys-admin-audit-time">
              {new Date(ev.created_at).toLocaleString()}
            </div>
          </div>
        );
      })
    )}
  </div>
);

// ---------------------------------------------------------------------------
// User row
// ---------------------------------------------------------------------------

interface UserRowProps {
  user: AdminUser;
  adminEmail: string;
  onRefresh: () => void;
}

const UserRow: React.FC<UserRowProps> = ({ user, adminEmail, onRefresh }) => {
  const [busy, setBusy] = useState(false);

  const confirm = (title: string, content: React.ReactNode, onOk: () => Promise<void>) => {
    Modal.confirm({
      title,
      content,
      okText: 'Confirm',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk,
    });
  };

  const withReason = (
    title: string,
    onOk: (reason: string) => Promise<void>,
    required = true,
  ) => {
    // Use a ref-like object so the captured value is always current when onOk fires,
    // regardless of when React processes the synthetic events.
    const reasonRef = { current: '' };
    Modal.confirm({
      title,
      content: (
        <div style={{ marginTop: 8 }}>
          <textarea
            autoFocus
            rows={3}
            placeholder={required ? 'Reason (required)' : 'Reason (optional)'}
            style={{
              width: '100%', padding: '8px', background: '#1f1f1f',
              border: '1px solid #3f3f3f', borderRadius: 6, color: '#ececec',
              fontFamily: 'inherit', resize: 'vertical',
            }}
            onChange={e => { reasonRef.current = e.target.value; }}
          />
        </div>
      ),
      okText: 'Confirm',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        const reason = reasonRef.current.trim();
        if (required && !reason) {
          message.error('Reason is required for this action.');
          return Promise.reject();
        }
        await onOk(reason);
      },
    });
  };

  const handleLock = () => {
    withReason(
      `Lock "${user.user_id}"?`,
      async (reason) => {
        setBusy(true);
        try {
          await api.lockUser(adminEmail, user.user_id, reason);
          message.success('User locked');
          onRefresh();
        } catch (e: any) {
          message.error('Failed to lock: ' + (e?.response?.data?.detail || e?.message));
        } finally { setBusy(false); }
      },
      false,
    );
  };

  const handleUnlock = async () => {
    confirm(
      `Unlock "${user.user_id}"?`,
      'This will restore full access for this user.',
      async () => {
        setBusy(true);
        try {
          await api.unlockUser(adminEmail, user.user_id);
          message.success('User unlocked');
          onRefresh();
        } catch (e: any) {
          message.error('Failed to unlock: ' + (e?.response?.data?.detail || e?.message));
        } finally { setBusy(false); }
      },
    );
  };

  const handleRemove = () => {
    withReason(
      `Deactivate "${user.user_id}"?`,
      async (reason) => {
        setBusy(true);
        try {
          await api.removeUser(adminEmail, user.user_id, reason);
          message.success('User deactivated');
          onRefresh();
        } catch (e: any) {
          message.error('Failed to remove: ' + (e?.response?.data?.detail || e?.message));
        } finally { setBusy(false); }
      },
      false,
    );
  };

  const handleRestore = () => {
    confirm(
      `Restore "${user.user_id}"?`,
      'This will set the user back to active status.',
      async () => {
        setBusy(true);
        try {
          await api.restoreUser(adminEmail, user.user_id);
          message.success('User restored');
          onRefresh();
        } catch (e: any) {
          message.error('Failed to restore: ' + (e?.response?.data?.detail || e?.message));
        } finally { setBusy(false); }
      },
    );
  };

  const handleClearData = () => {
    withReason(
      `⚠ Permanently delete ALL data for "${user.user_id}"?`,
      async (reason) => {
        setBusy(true);
        try {
          await api.clearUserData(adminEmail, user.user_id, reason);
          message.success('All user data cleared');
          onRefresh();
        } catch (e: any) {
          message.error('Failed to clear data: ' + (e?.response?.data?.detail || e?.message));
        } finally { setBusy(false); }
      },
      true,
    );
  };

  const handleExport = () => {
    const url = api.exportUserDataUrl(user.user_id, adminEmail);
    const a = document.createElement('a');
    a.href = url;
    a.download = '';
    a.click();
  };

  const stats = user.stats;
  const isSelf = user.user_id.toLowerCase() === adminEmail.toLowerCase();

  return (
    <div className="praxys-admin-user-row">
      <div className="praxys-admin-user-info">
        <div className="praxys-admin-user-avatar">
          {(user.display_name || user.user_id).charAt(0).toUpperCase()}
        </div>
        <div className="praxys-admin-user-copy">
          <div className="praxys-admin-user-id">{user.user_id}</div>
          {user.notes && (
            <div className="praxys-admin-user-notes">{user.notes}</div>
          )}
          {stats && (
            <div className="praxys-admin-user-stats">
              {stats.tasks_total} task{stats.tasks_total === 1 ? '' : 's'} ·{' '}
              {stats.kb_total} knowledge base{stats.kb_total === 1 ? '' : 's'} ·{' '}
              {stats.memory_total} memory ·{' '}
              {stats.tokens_active}/{stats.tokens_total} tokens
            </div>
          )}
        </div>
      </div>
      <div className="praxys-admin-user-mid">
        <span
          className="praxys-admin-status-badge"
          style={{ background: STATUS_COLOR[user.status] + '22', color: STATUS_COLOR[user.status] }}
        >
          {STATUS_LABEL[user.status] || user.status}
        </span>
        <span className="praxys-admin-user-since">
          since {new Date(user.created_at).toLocaleDateString()}
        </span>
      </div>
      <div className="praxys-admin-user-actions">
        {user.status === 'active' && !isSelf && (
          <button
            type="button"
            className="praxys-admin-btn praxys-admin-btn--warn"
            onClick={handleLock}
            disabled={busy}
            title="Lock user"
          >
            <LockOutlined /> Lock
          </button>
        )}
        {user.status === 'locked' && (
          <button
            type="button"
            className="praxys-admin-btn"
            onClick={handleUnlock}
            disabled={busy}
            title="Unlock user"
          >
            <UnlockOutlined /> Unlock
          </button>
        )}
        {(user.status === 'active' || user.status === 'locked') && !isSelf && (
          <button
            type="button"
            className="praxys-admin-btn praxys-admin-btn--danger"
            onClick={handleRemove}
            disabled={busy}
            title="Deactivate user"
          >
            <UserDeleteOutlined /> Remove
          </button>
        )}
        {user.status === 'removed' && (
          <button
            type="button"
            className="praxys-admin-btn"
            onClick={handleRestore}
            disabled={busy}
            title="Restore user"
          >
            <ReloadOutlined /> Restore
          </button>
        )}
        <button
          type="button"
          className="praxys-admin-btn praxys-admin-btn--danger"
          onClick={handleClearData}
          disabled={busy || isSelf}
          title={isSelf ? 'Cannot clear your own data' : 'Permanently delete all user data'}
        >
          <ClearOutlined /> Clear Data
        </button>
        <button
          type="button"
          className="praxys-admin-btn"
          onClick={handleExport}
          disabled={busy}
          title="Export all user data"
        >
          <DownloadOutlined /> Export
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main AdminView
// ---------------------------------------------------------------------------

const AdminView: React.FC<Props> = ({ user }) => {
  const adminEmail = user?.email || '';

  const [tab, setTab] = useState<AdminTab>('users');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listAdminUsers(adminEmail, statusFilter || undefined);
      const nextUsers = res?.data?.users;
      setUsers(Array.isArray(nextUsers) ? nextUsers : []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [adminEmail, statusFilter]);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await api.getAdminMetrics(adminEmail);
      setMetrics(res.data);
    } catch { /* metrics are non-critical */ }
  }, [adminEmail]);

  const fetchAuditLog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getAdminAuditLog(adminEmail, 100);
      setAuditEvents(Array.isArray(res.data?.events) ? res.data.events : []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [adminEmail]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    if (tab === 'users') {
      fetchUsers();
    } else if (tab === 'metrics') {
      fetchMetrics();
    } else if (tab === 'audit') {
      fetchAuditLog();
    }
  }, [tab, fetchUsers, fetchMetrics, fetchAuditLog]);

  const filteredUsers = (users ?? []).filter(u => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return u.user_id.toLowerCase().includes(q) || u.display_name.toLowerCase().includes(q);
  });

  if (!user) {
    return (
      <div className="praxys-view-panel">
        <div className="praxys-view-empty">Not logged in.</div>
      </div>
    );
  }

  return (
    <div className="praxys-view-panel">
      <div className="praxys-view-header">
        <SettingOutlined className="praxys-view-icon" />
        <h2 className="praxys-view-title">Admin</h2>
        <span className="praxys-view-subtitle">System administration · {adminEmail}</span>
      </div>

      {/* Tabs */}
      <div className="praxys-admin-tabs">
        <button
          type="button"
          className={`praxys-admin-tab ${tab === 'users' ? 'active' : ''}`}
          onClick={() => setTab('users')}
        >
          <TeamOutlined /> Users
        </button>
        <button
          type="button"
          data-help="beta-invites-tab"
          className={`praxys-admin-tab ${tab === 'invites' ? 'active' : ''}`}
          onClick={() => setTab('invites')}
        >
          <MailOutlined /> Beta Invites
        </button>
        <button
          type="button"
          className={`praxys-admin-tab ${tab === 'metrics' ? 'active' : ''}`}
          onClick={() => setTab('metrics')}
        >
          <DatabaseOutlined /> Metrics
        </button>
        <button
          type="button"
          className={`praxys-admin-tab ${tab === 'audit' ? 'active' : ''}`}
          onClick={() => setTab('audit')}
        >
          <AuditOutlined /> Audit Log
        </button>
        <button
          type="button"
          className={`praxys-admin-tab ${tab === 'requests' ? 'active' : ''}`}
          onClick={() => setTab('requests')}
        >
          <DatabaseOutlined /> New Requests
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="praxys-admin-error">
          <WarningOutlined style={{ marginRight: 8 }} />{error}
        </div>
      )}

      {/* ── Users tab ── */}
      {tab === 'users' && (
        <section className="praxys-admin-section">
          <div className="praxys-admin-toolbar">
            <div className="praxys-admin-search-wrap">
              <SearchOutlined className="praxys-admin-search-icon" />
              <input
                className="praxys-admin-search"
                placeholder="Search users…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <select
              className="praxys-admin-filter"
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); }}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="locked">Locked</option>
              <option value="removed">Removed</option>
            </select>
            <button
              type="button"
              className="praxys-admin-btn"
              onClick={fetchUsers}
              disabled={loading}
              title="Refresh user list"
            >
              <ReloadOutlined /> Refresh
            </button>
          </div>

          {loading ? (
            <div className="praxys-view-empty">Loading users…</div>
          ) : filteredUsers.length === 0 ? (
            <div className="praxys-view-empty">
              {users.length === 0
                ? 'No users found. Users are auto-discovered once tasks or knowledge bases are created.'
                : 'No users match your search.'}
            </div>
          ) : (
            <div className="praxys-admin-user-list">
              {filteredUsers.map(u => (
                <UserRow
                  key={u.user_id}
                  user={u}
                  adminEmail={adminEmail}
                  onRefresh={fetchUsers}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {tab === 'invites' && (
        <section className="praxys-admin-section">
          <BetaInvitesPanel adminEmail={adminEmail} />
        </section>
      )}

      {/* ── Metrics tab ── */}
      {tab === 'metrics' && (
        <section className="praxys-admin-section">
          {metrics ? (
            <>
              <MetricsPanel metrics={metrics} />
              <div className="praxys-admin-metric-generated">
                Generated {new Date(metrics.generated_at).toLocaleString()}
                <button
                  type="button"
                  className="praxys-admin-btn"
                  onClick={fetchMetrics}
                  style={{ marginLeft: 12 }}
                >
                  <ReloadOutlined /> Refresh
                </button>
              </div>
            </>
          ) : (
            <div className="praxys-view-empty">Loading metrics…</div>
          )}
        </section>
      )}

      {/* ── Audit log tab ── */}
      {tab === 'audit' && (
        <section className="praxys-admin-section">
          <div className="praxys-admin-toolbar">
            <span style={{ color: '#9ca3af', fontSize: 13 }}>
              Last {(auditEvents ?? []).length} admin actions
            </span>
            <button
              type="button"
              className="praxys-admin-btn"
              onClick={fetchAuditLog}
              disabled={loading}
            >
              <ReloadOutlined /> Refresh
            </button>
          </div>
          {loading ? (
            <div className="praxys-view-empty">Loading audit log…</div>
          ) : (
            <AuditPanel events={auditEvents} />
          )}
        </section>
      )}

      {tab === 'requests' && (
        <section className="praxys-admin-section">
          <AdminPendingList adminEmail={adminEmail} />
        </section>
      )}
    </div>
  );
};

export default AdminView;
